import { Button, Flex } from '@chakra-ui/react';
import { useCallback, useEffect } from 'react';

import Dialog from '@app/components/common/modals/dialog';
import AutocompleteField from '@app/components/common/autocomplete-field';
import { SwitchField, TextField } from '@app/components/common';
import { useAlert, useAutocomplete, useEntityState } from '@app/hooks';

import { OutletDistributorForm } from '../types';

const emptyForm: OutletDistributorForm = {
  distributor_outlet_id: '',
  sfa_id: null,
  is_general: false,
  is_active: true,
};

export const OutletDistributorDialog = ({
  isVisible,
  excludedDistributorIds = [],
  item,
  isEdit,
  onClose,
  onSave,
}: {
  isVisible: boolean;
  excludedDistributorIds?: number[];
  item?: OutletDistributorForm;
  isEdit: boolean;
  onClose: () => void;
  onSave: (item: OutletDistributorForm) => Promise<boolean>;
}) => {
  const [instance, onChange, setInstance] = useEntityState<OutletDistributorForm>(emptyForm);
  const { error } = useAlert();
  const getAutocomplete = useAutocomplete();

  const fetchDistributors = useCallback((name: string, query: Record<string, string>) => (
    getAutocomplete(name, query).then((options) => (
      options.filter(({ id }) => !excludedDistributorIds.includes(Number(id)))
    ))
  ), [excludedDistributorIds, getAutocomplete]);

  useEffect(() => {
    setInstance(item || emptyForm);
  }, [item]);

  const onSaveClick = async () => {
    if (!isEdit && !instance.distributor) {
      error('Выберите дистрибьютора');
      return;
    }

    const isSaved = await onSave({
      is_active: true,
      is_general: false,
      ...instance,
      distributor_outlet_id: instance.distributor_outlet_id?.trim() ?? '',
      sfa_id: instance.sfa_id,
    });

    if (!isSaved) {
      return;
    }

    setInstance(emptyForm);
    onClose();
  };

  return (
    <Dialog
      open={isVisible}
      title={isEdit ? 'Редактирование дистрибьютора' : 'Добавление дистрибьютора'}
      onClose={onClose}
      actions={
        <Flex justifyContent="space-between" w="100%">
          <Button onClick={onSaveClick} variant="solid" colorScheme="brand">
            Сохранить
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </Flex>
      }
    >
      <Flex gap={4} alignItems="center" direction="column">
        {isEdit ? (
          <TextField
            value={instance.distributor?.name || ''}
            onChange={() => undefined}
            id="distributor"
            label="Дистрибьютор"
            readOnly
          />
        ) : (
          <AutocompleteField
            required
            id="distributor"
            label="Дистрибьютор"
            name="distributor"
            valueProperty="name"
            value={instance.distributor}
            onChange={(value) => {
              onChange('distributor')(value);
            }}
            onFetchOptions={fetchDistributors}
          />
        )}
        <TextField
          value={instance.distributor_outlet_id}
          onChange={(value) => {
            onChange('distributor_outlet_id')(value);
          }}
          id="distributor_outlet_id"
          label="ID ТТ в системе дистрибьютора"
        />
        <TextField
          value={instance.sfa_id?.toString() ?? null}
          onChange={onChange('sfa_id')}
          id="sfa_id"
          label="SFA ID"
        />
        <SwitchField
          id="is_general"
          label="Основной"
          value={instance.is_general}
          onChange={onChange('is_general')}
        />
        {isEdit && (
          <SwitchField
            id="is_active"
            label="Активен"
            value={instance.is_active}
            onChange={onChange('is_active')}
          />
        )}
      </Flex>
    </Dialog>
  );
};
